import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { EXPERIENCE_VARIABLE_NAME_MAX } from '@matter_extractor/shared';
import { TextField } from '@pms-digital/design-system';

/**
 * « Nom de variable », partagé par l'Input, la Checkbox et le Bouton qui retient
 * son choix (refonte modulaire §8.6, §9.2; planche `Inspecteurs`; issue #58).
 *
 * §8.6 is the whole of this field in two sentences: « le nom de variable qu'on
 * saisit dans l'inspecteur n'est QU'UN LIBELLÉ : ni une clé, ni une adresse »,
 * and it « doit être unique dans la timeline, vérifié au geste Enregistrer une
 * nouvelle version ». So the field says both - what it is for, and that a
 * rename costs nothing - and it names the duplicate.
 *
 * ## Le doublon se dit à la FRAPPE, et se refuse à l'enregistrement
 *
 * The server answers `duplicate-variable-name` at the save and the dry run of
 * the box words it (§9.1). That is the authority, and it is also the moment
 * somebody has stopped typing. Saying it here as well costs one pure function
 * (`duplicateVariableNames`) and turns a refusal into a correction: the two
 * count the same thing - `namedVariablesOf` - so they cannot disagree.
 *
 * The package `TextField` draws both lines: the hint is its `help`, the
 * duplicate its error `message`, which takes the hint's place as the field's
 * description and sets `aria-invalid`.
 */

export interface VariableNameFieldProps {
  readonly value: string;
  readonly readOnly: boolean;
  /** The name is declared twice or more in the timeline (§8.6). */
  readonly duplicate: boolean;
  readonly onChange: (next: string) => void;
}

export function VariableNameField({
  value,
  readOnly,
  duplicate,
  onChange,
}: VariableNameFieldProps) {
  return (
    <TextField
      label="Nom de variable"
      compact
      data-testid={PAGE_EDITOR_IDS.blockVariable}
      value={value}
      readOnly={readOnly}
      maxLength={EXPERIENCE_VARIABLE_NAME_MAX}
      onChange={(event) => onChange(event.currentTarget.value)}
      status={duplicate ? 'error' : 'default'}
      message={
        duplicate ? (
          <span data-testid={PAGE_EDITOR_IDS.variableDuplicate}>
            Une autre variable de la timeline porte déjà « {value} ». L’enregistrement d’une version
            sera refusé tant que les deux noms sont les mêmes.
          </span>
        ) : undefined
      }
      help={`Un libellé, unique dans la timeline ; le jeton stocké porte l’identifiant du bloc, donc renommer ne casse rien.${value === '' ? '' : ` Insérable ailleurs par @${value}.`}`}
    />
  );
}
